import { useEffect, useMemo, useRef } from 'react';
import ApexCharts from 'apexcharts';
import { useDemoCtx } from './DemoContext';
import {
    getMoistStatus,
    MOIST_COLOR,
    MOIST_LABEL,
    treeEmoji,
    type SavedSite,
} from './demoStorage';

/* ── utils ── */
function parseNum(s: string): number {
    return parseInt((s || '0').replace(/,/g, ''), 10) || 0;
}

/* ═══════════════════════════════════════
   Chart 1 — 수분 상태 도넛
═══════════════════════════════════════ */
function StatusDonut({ sites }: { sites: SavedSite[] }) {
    const ref  = useRef<HTMLDivElement>(null);
    const inst = useRef<ApexCharts | null>(null);

    useEffect(() => {
        if (!ref.current) return;
        const counts = { good: 0, caution: 0, warn: 0, danger: 0 };
        sites.forEach(s => { counts[getMoistStatus(s.moist ?? 50)]++; });
        const series = [counts.good, counts.caution, counts.warn, counts.danger];
        const total  = series.reduce((a, b) => a + b, 0);

        const opts = {
            series,
            chart: { type: 'donut', height: 250, toolbar: { show: false },
                fontFamily: 'Pretendard,-apple-system,sans-serif' },
            labels: ['양호', '주의', '위험', '심각'],
            colors: ['#1E8B53', '#2563EB', '#D97706', '#DC2626'],
            plotOptions: {
                pie: {
                    donut: {
                        size: '62%',
                        labels: {
                            show: true,
                            name:  { fontSize: '12px', fontFamily: 'Pretendard,-apple-system,sans-serif' },
                            value: { fontSize: '20px', fontWeight: 800, color: '#1A2B1A',
                                fontFamily: 'Pretendard,-apple-system,sans-serif' },
                            total: {
                                show: true, label: '전체 단지', fontSize: '11px', color: '#6B7280',
                                fontFamily: 'Pretendard,-apple-system,sans-serif',
                                formatter: () => `${total}개`,
                            },
                        },
                    },
                },
            },
            legend:      { position: 'bottom', fontSize: '11px', offsetY: 4 },
            dataLabels:  { enabled: false },
            stroke:      { width: 0 },
            tooltip:     { y: { formatter: (v: number) => `${v}개 단지` } },
        };

        inst.current?.destroy();
        inst.current = new ApexCharts(ref.current, opts);
        inst.current.render();
        return () => { inst.current?.destroy(); inst.current = null; };
    }, [sites]);

    return <div ref={ref} />;
}

/* ═══════════════════════════════════════
   Chart 2 — 월간 관수량 추이
═══════════════════════════════════════ */
const MONTHS     = ['8월','9월','10월','11월','12월','1월'];
const WATER_DATA = [8400, 9200, 7800, 8100, 6900, 7300];
const RAIN_DATA  = [85, 62, 48, 32, 28, 42];

function MonthlyChart() {
    const ref  = useRef<HTMLDivElement>(null);
    const inst = useRef<ApexCharts | null>(null);

    useEffect(() => {
        if (!ref.current) return;
        const opts = {
            series: [
                { name: '관수량(백L)', type: 'area', data: WATER_DATA },
                { name: '강수량(mm)',  type: 'bar',  data: RAIN_DATA  },
            ],
            chart: { type: 'line', height: 230, toolbar: { show: false },
                fontFamily: 'Pretendard,-apple-system,sans-serif' },
            stroke:      { curve: ['smooth','smooth'], width: [2.5, 0] },
            plotOptions: { bar: { columnWidth: '48%', borderRadius: 3 } },
            colors: ['#1E8B53', '#93C5FD'],
            fill: {
                type: ['gradient','solid'],
                gradient: { shade: 'light', type: 'vertical', opacityFrom: 0.35, opacityTo: 0.03 },
                opacity: [1, 0.7],
            },
            grid:        { borderColor: '#f0f5f0', strokeDashArray: 3 },
            dataLabels:  { enabled: false },
            legend:      { show: true, position: 'top', markers: { size: 5 }, fontSize: '11px' },
            xaxis: {
                categories: MONTHS,
                labels: { style: { fontSize: '11px' } },
                axisBorder: { show: false }, axisTicks: { show: false },
            },
            yaxis: [
                { title: { text: '관수량(백L)', style: { fontSize: '10px' } },
                    labels: { formatter: (v: number) => `${v}`, style: { fontSize: '9px' } } },
                { opposite: true, title: { text: '강수량(mm)', style: { fontSize: '10px' } },
                    labels: { formatter: (v: number) => `${v}mm`, style: { fontSize: '9px' } } },
            ],
            tooltip: { shared: true, intersect: false },
        };
        inst.current?.destroy();
        inst.current = new ApexCharts(ref.current, opts);
        inst.current.render();
        return () => { inst.current?.destroy(); inst.current = null; };
    }, []);

    return <div ref={ref} />;
}

/* ═══════════════════════════════════════
   Chart 3 — 단지별 수분율 가로 바
═══════════════════════════════════════ */
function MoistureBar({ sites }: { sites: SavedSite[] }) {
    const ref  = useRef<HTMLDivElement>(null);
    const inst = useRef<ApexCharts | null>(null);

    useEffect(() => {
        if (!ref.current || !sites.length) return;
        const sorted = [...sites].sort((a, b) => (b.moist ?? 50) - (a.moist ?? 50));
        const top    = sorted.slice(0, Math.min(12, sorted.length));
        const h      = Math.max(240, top.length * 34 + 60);

        const opts = {
            series: [{ name: '수분율(%)', data: top.map(s => s.moist ?? 50) }],
            chart: { type: 'bar', height: h, toolbar: { show: false },
                fontFamily: 'Pretendard,-apple-system,sans-serif' },
            plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 4, distributed: true } },
            colors: top.map(s => MOIST_COLOR[getMoistStatus(s.moist ?? 50)]),
            dataLabels: {
                enabled: true, formatter: (v: number) => `${v}%`,
                style: { fontSize: '10px', fontWeight: 700 }, offsetX: 4,
            },
            xaxis: {
                categories: top.map(s => {
                    const ico = treeEmoji(s.form.treeName);
                    return `${ico} ${s.form.name || '미입력'}`;
                }),
                min: 0, max: 100,
                labels: { style: { fontSize: '10px' } },
                axisBorder: { show: false }, axisTicks: { show: false },
            },
            yaxis: { labels: { style: { fontSize: '10px', fontWeight: 600 } } },
            grid:   { borderColor: '#f0f5f0', strokeDashArray: 3, yaxis: { lines: { show: false } } },
            legend: { show: false },
            annotations: {
                xaxis: [
                    { x: 55, borderColor: '#1E8B53', strokeDashArray: 4,
                        label: { text: '양호 55%', orientation: 'horizontal',
                            style: { color: '#1E8B53', background: '#D1FAE5', fontSize: '9px', fontWeight: 700 } } },
                    { x: 40, borderColor: '#DC2626', strokeDashArray: 4,
                        label: { text: '위험 40%', orientation: 'horizontal',
                            style: { color: '#DC2626', background: '#FEE2E2', fontSize: '9px', fontWeight: 700 } } },
                ],
            },
            tooltip: { y: { formatter: (v: number) => `${v}%` } },
        };

        inst.current?.destroy();
        inst.current = new ApexCharts(ref.current, opts);
        inst.current.render();
        return () => { inst.current?.destroy(); inst.current = null; };
    }, [sites]);

    return <div ref={ref} />;
}

/* ═══════════════════════════════════════
   Main Component
═══════════════════════════════════════ */
export default function DemoReports() {
    const { toast, savedSites } = useDemoCtx();

    const stats = useMemo(() => {
        const totalArea  = savedSites.reduce((s, site) => s + parseNum(site.form.area),      0);
        const totalTrees = savedSites.reduce((s, site) => s + parseNum(site.form.treeCount), 0);
        const counts     = { good: 0, caution: 0, warn: 0, danger: 0 };
        savedSites.forEach(s => { counts[getMoistStatus(s.moist ?? 50)]++; });
        const goodRate = savedSites.length
            ? Math.round(counts.good / savedSites.length * 100) : 0;
        const avgMoist = savedSites.length
            ? Math.round(savedSites.reduce((s, x) => s + (x.moist ?? 50), 0) / savedSites.length) : 0;

        const speciesMap: Record<string, number> = {};
        savedSites.forEach(site => {
            const names = (site.form.treeName || '').split(/[·,/]/).map(n => n.trim()).filter(Boolean);
            const cnt   = parseNum(site.form.treeCount) || 1;
            names.forEach(n => {
                speciesMap[n] = (speciesMap[n] || 0) + Math.ceil(cnt / names.length);
            });
        });
        const topSpecies    = Object.entries(speciesMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
        const maxSpeciesCnt = topSpecies[0]?.[1] || 1;

        const drainageBad   = savedSites.filter(s => s.form.drainage === '심각' || s.form.drainage === '불량').length;
        const plantDateRate = Math.round(savedSites.filter(s => s.form.treePlantDate).length / Math.max(savedSites.length, 1) * 100);

        return { totalArea, totalTrees, counts, goodRate, avgMoist, topSpecies, maxSpeciesCnt, drainageBad, plantDateRate };
    }, [savedSites]);

    const today = new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
    const urgentCnt = stats.counts.danger + stats.counts.warn;

    const KPI = [
        { lbl: '등록 단지',    val: `${savedSites.length}개`, sub: '전체 관리 단지',                     ico: '🏘️', col: '#1E8B53' },
        { lbl: '총 관리 면적', val: `${(stats.totalArea / 1000).toFixed(1)}천㎡`,
            sub: `${Math.round(stats.totalArea / 3.305 / 1000)}천평`,                                   ico: '📐', col: '#2563EB' },
        { lbl: '총 식재 수목', val: `${stats.totalTrees.toLocaleString()}주`, sub: '전체 등록 수종',      ico: '🌲', col: '#059669' },
        { lbl: '수분 양호율',  val: `${stats.goodRate}%`, sub: `${stats.counts.good}/${savedSites.length}개 단지 양호`, ico: '💧', col: '#1E8B53' },
        { lbl: '관수 필요',    val: `${urgentCnt}개`,     sub: '위험·심각 단지',                          ico: '⚠️', col: urgentCnt > 0 ? '#DC2626' : '#D97706' },
        { lbl: '평균 수분율',  val: `${stats.avgMoist}%`, sub: '전 단지 평균',                            ico: '📊', col: MOIST_COLOR[getMoistStatus(stats.avgMoist)] },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
            fontFamily: 'Pretendard,-apple-system,sans-serif' }}>

            {/* ── Topbar ── */}
            <div className="list-bar">
                <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>종합 보고서</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{today} 기준</div>
                </div>
                <div style={{ flex: 1 }} />
                <select className="form-select" style={{ width: 130, height: 32, fontSize: 12 }}>
                    <option>2026년 1월</option><option>2025년 12월</option><option>2025년 11월</option>
                </select>
                <select className="form-select" style={{ width: 140, height: 32, fontSize: 12 }}>
                    <option>전체 단지</option>
                </select>
                <button className="btn btn-secondary btn-sm" onClick={() => toast('PDF 생성 중...', 'success')}>📄 PDF</button>
                <button className="btn btn-secondary btn-sm" onClick={() => toast('Excel 내보내기 완료', 'success')}>⬇ Excel</button>
                <button className="btn btn-ghost btn-sm"      onClick={() => window.print()}>🖨 인쇄</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#F4F7F4', display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* ── 보고서 헤더 ── */}
                <div style={{
                    borderRadius: 16, padding: '22px 28px',
                    background: 'linear-gradient(135deg, #1A6B3E 0%, #0F4F2E 100%)',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    boxShadow: '0 4px 20px rgba(30,139,83,0.3)',
                }}>
                    <div>
                        <div style={{ fontSize: 10.5, fontWeight: 600, opacity: 0.7, letterSpacing: '0.1em', marginBottom: 6,
                            textTransform: 'uppercase' }}>Smart TreeMap · Comprehensive Report</div>
                        <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                            생육단지 통합 현황 보고서
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span>📅 2026년 1월 기준</span>
                            <span>·</span>
                            <span>🏘️ 전체 {savedSites.length}개 단지</span>
                            <span>·</span>
                            <span>📋 {today} 발행</span>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 36, marginBottom: 4 }}>🌿</div>
                        <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.9 }}>(주)그린조경</div>
                        <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>수목 스마트 관리 플랫폼</div>
                    </div>
                </div>

                {/* ── KPI 카드 6개 ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 12 }}>
                    {KPI.map((k, i) => (
                        <div key={i} style={{
                            background: '#fff', borderRadius: 12, padding: '15px 16px',
                            border: `1.5px solid ${k.col}20`,
                            boxShadow: `0 2px 10px ${k.col}0d`,
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                                <div style={{ fontSize: 10.5, color: 'var(--text3)', fontWeight: 600, lineHeight: 1.3 }}>{k.lbl}</div>
                                <span style={{ fontSize: 20 }}>{k.ico}</span>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 900, color: k.col, letterSpacing: '-0.02em', lineHeight: 1 }}>{k.val}</div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>{k.sub}</div>
                        </div>
                    ))}
                </div>

                {/* ── Row 1: 도넛 + 월간 추이 ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 14 }}>
                    <div className="data-card" style={{ margin: 0 }}>
                        <div className="data-card-hd">
                            <span className="data-card-ttl">수분 상태 분포</span>
                            <span style={{ fontSize: 10, color: 'var(--text3)' }}>전체 {savedSites.length}개 단지</span>
                        </div>
                        <StatusDonut sites={savedSites} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                            {(['good','caution','warn','danger'] as const).map(st => (
                                <div key={st} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                    background: `${MOIST_COLOR[st]}12`, borderRadius: 8, padding: '8px 10px' }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: MOIST_COLOR[st], flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{MOIST_LABEL[st]}</div>
                                        <div style={{ fontSize: 16, fontWeight: 900, color: MOIST_COLOR[st], lineHeight: 1.2 }}>
                                            {stats.counts[st]}<span style={{ fontSize: 10, fontWeight: 600 }}>개</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="data-card" style={{ margin: 0 }}>
                        <div className="data-card-hd">
                            <span className="data-card-ttl">월간 관수량 & 강수량 추이</span>
                            <span style={{ fontSize: 11, background: '#D1FAE5', color: '#166534',
                                borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>
                                전월比 −5.7% ↓
                            </span>
                        </div>
                        <MonthlyChart />
                        <div style={{ display: 'flex', gap: 16, marginTop: 8, padding: '10px 14px',
                            background: '#F8FAF8', borderRadius: 8 }}>
                            {[
                                { lbl: '이달 총 관수량', val: '730백L', ico: '💧', col: '#1E8B53' },
                                { lbl: '이달 강수량',    val: '42mm',   ico: '🌧️', col: '#2563EB' },
                                { lbl: '절감 효율',      val: '5.7%',   ico: '📉', col: '#059669' },
                            ].map((d, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <span style={{ fontSize: 18 }}>{d.ico}</span>
                                    <div>
                                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{d.lbl}</div>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: d.col }}>{d.val}</div>
                                    </div>
                                    {i < 2 && <div style={{ width: 1, height: 28, background: 'var(--border)', margin: '0 4px' }} />}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Row 2: 단지별 수분율 풀 와이드 ── */}
                <div className="data-card" style={{ margin: 0 }}>
                    <div className="data-card-hd">
                        <span className="data-card-ttl">단지별 현재 수분율 현황</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: 10.5, color: '#1E8B53', fontWeight: 700,
                                background: '#D1FAE5', borderRadius: 20, padding: '2px 8px' }}>양호 ≥ 55%</span>
                            <span style={{ fontSize: 10.5, color: '#DC2626', fontWeight: 700,
                                background: '#FEE2E2', borderRadius: 20, padding: '2px 8px' }}>위험 &lt; 40%</span>
                        </div>
                    </div>
                    <MoistureBar sites={savedSites} />
                </div>

                {/* ── Row 3: 수종 분포 + 관리 지표 ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div className="data-card" style={{ margin: 0 }}>
                        <div className="data-card-hd">
                            <span className="data-card-ttl">수종 분포 현황</span>
                            <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                                {Object.keys(stats.topSpecies.reduce((m, [n]) => ({ ...m, [n]: 1 }), {})).length}개 수종
                            </span>
                        </div>
                        {stats.topSpecies.length === 0 ? (
                            <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '28px 0' }}>
                                수종 데이터가 없습니다
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {stats.topSpecies.map(([name, cnt], i) => {
                                    const pct   = Math.round(cnt / stats.maxSpeciesCnt * 100);
                                    const ico   = treeEmoji(name);
                                    const total = stats.topSpecies.reduce((s, [, c]) => s + c, 0);
                                    const share = Math.round(cnt / total * 100);
                                    return (
                                        <div key={i}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                                                <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{ico}</span>
                                                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)', flex: 1 }}>{name}</span>
                                                <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{share}%</span>
                                                <span style={{ fontSize: 13, fontWeight: 800, color: '#1E8B53', minWidth: 40, textAlign: 'right' }}>
                                                    {cnt}주
                                                </span>
                                            </div>
                                            <div style={{ height: 7, background: '#E5F0E5', borderRadius: 4, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4,
                                                    background: 'linear-gradient(90deg, #1E8B53, #34D399)',
                                                    transition: 'width 0.6s ease' }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="data-card" style={{ margin: 0 }}>
                        <div className="data-card-hd"><span className="data-card-ttl">토양 및 관리 지표</span></div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[
                                { lbl: '평균 토양 pH',   val: '6.5',                                            icon: '🧪', note: '적정 범위 6.0~7.0', ok: true  },
                                { lbl: '평균 토심',      val: '30cm',                                           icon: '📏', note: '표준 깊이 유지',    ok: true  },
                                { lbl: '배수 불량 단지', val: `${stats.drainageBad}개`,                          icon: '🚨', note: '즉각 개선 필요',   ok: stats.drainageBad === 0 },
                                { lbl: '식재일 기록률',  val: `${stats.plantDateRate}%`,                        icon: '📅', note: '이력 관리 현황',    ok: stats.plantDateRate >= 80 },
                                { lbl: '담당자 배정률',  val: `${Math.round(savedSites.filter(s => s.form.mgr).length / Math.max(savedSites.length, 1) * 100)}%`, icon: '👤', note: '전담 관리 현황', ok: true },
                                { lbl: '긴급 관수 필요', val: `${stats.counts.danger}개 단지`,                   icon: '💧', note: '당일 처리 권고',    ok: stats.counts.danger === 0 },
                            ].map((row, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '9px 12px', borderRadius: 9,
                                    background: row.ok ? '#F0FDF4' : '#FFF1F2',
                                    border: `1px solid ${row.ok ? '#BBF7D0' : '#FECDD3'}` }}>
                                    <span style={{ fontSize: 20, flexShrink: 0 }}>{row.icon}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{row.lbl}</div>
                                        <div style={{ fontSize: 14, fontWeight: 800,
                                            color: row.ok ? '#166534' : '#DC2626' }}>{row.val}</div>
                                    </div>
                                    <div style={{ fontSize: 10, fontWeight: 600, flexShrink: 0,
                                        color: row.ok ? '#15803D' : '#DC2626' }}>{row.note}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── 전체 단지 상세 테이블 ── */}
                <div className="data-card" style={{ margin: 0 }}>
                    <div className="data-card-hd">
                        <span className="data-card-ttl">전체 등록 단지 상세 현황</span>
                        <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>총 {savedSites.length}개 단지</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-tbl" style={{ fontSize: 12 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 30 }}>#</th>
                                    <th>상태</th>
                                    <th>단지명</th>
                                    <th>주요 수종</th>
                                    <th>그루수</th>
                                    <th>면적(㎡)</th>
                                    <th style={{ minWidth: 120 }}>수분율</th>
                                    <th>토양 pH</th>
                                    <th>배수</th>
                                    <th>담당자</th>
                                    <th>식재일</th>
                                    <th>등록일</th>
                                </tr>
                            </thead>
                            <tbody>
                                {savedSites.length === 0 && (
                                    <tr>
                                        <td colSpan={12} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text3)' }}>
                                            등록된 단지가 없습니다
                                        </td>
                                    </tr>
                                )}
                                {savedSites.map((site, i) => {
                                    const moist = site.moist ?? 50;
                                    const st    = getMoistStatus(moist);
                                    const color = MOIST_COLOR[st];
                                    const badDrain = site.form.drainage === '심각' || site.form.drainage === '불량';
                                    return (
                                        <tr key={site.id}>
                                            <td style={{ color: 'var(--text3)', fontSize: 11 }}>{i + 1}</td>
                                            <td>
                                                <span className={`badge badge-${st}`}>
                                                    <span className={`badge-dot dot-${st}`} />
                                                    {MOIST_LABEL[st]}
                                                </span>
                                            </td>
                                            <td className="bold">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ fontSize: 15 }}>{treeEmoji(site.form.treeName)}</span>
                                                    {site.form.name || '(미입력)'}
                                                </div>
                                            </td>
                                            <td>
                                                {site.form.treeName ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
                                                        background: '#F0FDF4', border: '1px solid #BBF7D0',
                                                        borderRadius: 5, padding: '2px 7px',
                                                        fontSize: 11, fontWeight: 700, color: '#166534', whiteSpace: 'nowrap' }}>
                                                        {site.form.treeName}
                                                    </span>
                                                ) : <span style={{ color: 'var(--text3)' }}>-</span>}
                                            </td>
                                            <td className="text-mono">{site.form.treeCount ? `${site.form.treeCount}주` : '-'}</td>
                                            <td className="text-mono">{site.form.area || '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                                    <div style={{ flex: 1, height: 5, background: '#E5F0E5', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
                                                        <div style={{ width: `${moist}%`, height: '100%', background: color, borderRadius: 3 }} />
                                                    </div>
                                                    <span style={{ color, fontWeight: 800, fontSize: 11, minWidth: 32 }}>{moist}%</span>
                                                </div>
                                            </td>
                                            <td className="text-mono">{site.form.ph || '-'}</td>
                                            <td>
                                                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 7px',
                                                    background: badDrain ? '#FEE2E2' : '#F0FDF4',
                                                    color: badDrain ? '#DC2626' : '#166534' }}>
                                                    {site.form.drainage || '-'}
                                                </span>
                                            </td>
                                            <td>{site.form.mgr || '-'}</td>
                                            <td className="text-mono" style={{ fontSize: 11, color: 'var(--text3)' }}>
                                                {site.form.treePlantDate || '-'}
                                            </td>
                                            <td className="text-mono" style={{ fontSize: 10.5, color: 'var(--text3)' }}>
                                                {new Date(site.createdAt).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' })}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── AI 종합 인사이트 ── */}
                <div className="data-card" style={{ margin: 0 }}>
                    <div className="data-card-hd">
                        <span className="data-card-ttl">🤖 AI 종합 인사이트 & 권고사항</span>
                        <span style={{ fontSize: 10, background: '#EDE9FE', color: '#7C3AED',
                            borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>AI 자동 분석</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {[
                            { ico: '💧', col: '#2563EB', bg: '#EFF6FF', bd: '#BFDBFE', title: '관수 효율 분석',
                                body: `전체 ${savedSites.length}개 단지 중 ${urgentCnt}개 단지가 즉각 관수가 필요합니다. 수분율 40% 미만 ${stats.counts.danger}개 단지는 오늘 중 관수 처리를 권장드립니다. 스마트 스케줄링 적용 시 관수량 최대 15% 절감이 가능합니다.` },
                            { ico: '🌡️', col: '#D97706', bg: '#FFFBEB', bd: '#FDE68A', title: '기후 영향 평가',
                                body: `이달 강수량(42mm)이 평년 대비 34% 부족합니다. 특히 서·남향 단지는 증발산량이 높아 관수 주기를 20% 단축하는 것을 권장합니다. 멀칭 처리로 토양 수분 증발을 30%까지 억제할 수 있습니다.` },
                            { ico: '🌿', col: '#059669', bg: '#ECFDF5', bd: '#6EE7B7', title: '수종별 관리 권고',
                                body: `벚나무·이팝나무 군락은 봄철 개화기 전 집중 관수가 필요합니다. 메타세쿼이아 단지는 뿌리 깊이까지 충분히 적셔주는 심층 관수를 권장하며, 소나무·잣나무 군락은 과습에 주의하여 배수 관리에 중점을 두세요.` },
                            { ico: '📈', col: '#7C3AED', bg: '#F5F3FF', bd: '#C4B5FD', title: '2월 예측 & 계획',
                                body: `2월 예상 강수량은 35mm로 평년보다 낮을 전망입니다. 배수 불량 ${stats.drainageBad}개 단지에 대한 선제적 배수 개선 공사를 권장하며, 신규 식재 단지는 뿌리 활착을 위한 집중 관수 계획을 수립하세요.` },
                        ].map((item, i) => (
                            <div key={i} style={{ background: item.bg, borderRadius: 12, padding: '14px 16px',
                                border: `1px solid ${item.bd}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <span style={{ fontSize: 20 }}>{item.ico}</span>
                                    <span style={{ fontSize: 12.5, fontWeight: 800, color: item.col }}>{item.title}</span>
                                </div>
                                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.7, color: '#374151' }}>{item.body}</p>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: 14, padding: '10px 14px', background: '#F8FAF8', borderRadius: 8,
                        fontSize: 10.5, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6,
                        border: '1px solid var(--border)' }}>
                        <span style={{ flexShrink: 0 }}>ℹ️</span>
                        <span>본 보고서는 {today} 기준 {savedSites.length}개 단지 데이터를 바탕으로 AI가 자동 생성하였습니다.
                            실제 현장 점검과 병행하여 활용하시기 바랍니다. · 스마트 TreeMap v2.0 · (주)그린조경</span>
                    </div>
                </div>

            </div>
        </div>
    );
}
